<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { datePreset, dateRangeError } from 'dashboard/helper/tmkFiltersHelper';

const props = defineProps({
  modelValue: { type: Object, required: true },
  label: { type: String, required: true },
  required: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();
const inferMode = value => {
  if (!value.from && !value.to) return props.required ? 'day' : 'all';
  return !value.to || value.from === value.to ? 'day' : 'range';
};
const mode = ref(inferMode(props.modelValue));
const touched = ref(false);
const error = computed(() => {
  if (mode.value === 'all' && !props.required) return '';
  return dateRangeError(props.modelValue.from, props.modelValue.to, true);
});
const update = value => emit('update:modelValue', value);
const changeMode = event => {
  mode.value = event.target.value;
  touched.value = false;
  if (mode.value === 'all') update({ from: '', to: '' });
  if (mode.value === 'day') {
    update({ from: props.modelValue.from, to: props.modelValue.from });
  }
};
const changeDate = (key, value) => {
  touched.value = true;
  update(
    mode.value === 'day'
      ? { from: value, to: value }
      : { ...props.modelValue, [key]: value }
  );
};
const selectPreset = preset => {
  const dates = datePreset(preset);
  mode.value = dates.from === dates.to ? 'day' : 'range';
  touched.value = false;
  update(dates);
};
watch(
  () => props.modelValue,
  (value, previous) => {
    // Reset after Clear, while keeping a user-selected empty range editable.
    if (!value.from && !value.to && (previous.from || previous.to)) {
      mode.value = props.required ? 'day' : 'all';
      touched.value = false;
    }
  }
);
const validate = () => {
  touched.value = true;
  return !error.value;
};
defineExpose({ validate });
</script>

<template>
  <fieldset class="min-w-0 flex flex-col gap-2">
    <legend class="mb-2 text-sm font-medium text-n-slate-12">
      {{ label }}
    </legend>
    <div class="flex flex-wrap items-center gap-2">
      <select
        :value="mode"
        :aria-label="t('CHAT_LIST.TMK.DATE_MODE', { field: label })"
        data-testid="tmk-date-mode"
        class="!m-0 !h-9 !w-auto !min-w-36 !rounded-lg !border-n-weak !text-sm"
        @change="changeMode"
      >
        <option v-if="!required" value="all">
          {{ t('CHAT_LIST.TMK.ANY_DATE') }}
        </option>
        <option value="day">{{ t('CHAT_LIST.TMK.ONE_DAY') }}</option>
        <option value="range">{{ t('CHAT_LIST.TMK.DATE_RANGE') }}</option>
      </select>
      <template v-if="mode !== 'all'">
        <label class="min-w-0 flex-1">
          <span class="sr-only">{{ t('CHAT_LIST.TMK.DATE_FROM') }}</span>
          <input
            type="date"
            data-testid="tmk-date-from"
            :value="modelValue.from"
            :aria-invalid="touched && !!error"
            class="!m-0 !h-9 !w-full !rounded-lg !border-n-weak !text-sm"
            @change="changeDate('from', $event.target.value)"
          />
        </label>
        <template v-if="mode === 'range'">
          <span class="text-xs text-n-slate-10">{{
            t('CHAT_LIST.TMK.DATE_TO_CONNECTOR')
          }}</span>
          <label class="min-w-0 flex-1">
            <span class="sr-only">{{ t('CHAT_LIST.TMK.DATE_TO') }}</span>
            <input
              type="date"
              data-testid="tmk-date-to"
              :value="modelValue.to"
              :min="modelValue.from || undefined"
              :aria-invalid="touched && !!error"
              class="!m-0 !h-9 !w-full !rounded-lg !border-n-weak !text-sm"
              @change="changeDate('to', $event.target.value)"
            />
          </label>
        </template>
      </template>
    </div>
    <div class="flex flex-wrap gap-1.5">
      <button
        v-for="preset in ['today', 'yesterday', 'last7', 'last30']"
        :key="preset"
        type="button"
        class="rounded-md bg-n-slate-3 px-2 py-1 text-xs text-n-slate-11 hover:bg-n-slate-4"
        @click="selectPreset(preset)"
      >
        {{ t(`CHAT_LIST.TMK.PRESETS.${preset}`) }}
      </button>
    </div>
    <p class="!m-0 text-xs text-n-slate-10">
      {{ t('CHAT_LIST.TMK.DATE_HELP') }}
    </p>
    <p v-if="touched && error" role="alert" class="!m-0 text-xs text-n-ruby-11">
      {{ t(`CHAT_LIST.TMK.DATE_ERRORS.${error}`) }}
    </p>
  </fieldset>
</template>
